import { string, bool, ref } from "yup";
import {
    DIGIT_REGEX,
    NO_DIGIT_REGEX,
    LETTERS_REGEX,
    NO_LETTERS_REGEX,
    LETTERS_OR_DIGITS_WITHOUT_UPPER_LETTERS,
    WHITESPACE_REGEX,
} from "@finbackoffice/fe-core";
import { userInfoValidationSchema } from "./userInfoValidationSchema";

export const PASSWORD_RULE = string()
    .when("$configObj", ([config], schema) => {
        if (config) {
            return schema
                .min(
                    config.password.min_length,
                    config.t("validation_err_minMaxLength", {
                        min_length: config.password.min_length,
                        max_length: config.password.max_length,
                    }),
                )
                .max(
                    config.password.max_length,
                    config.t("validation_err_minMaxLength", {
                        min_length: config.password.min_length,
                        max_length: config.password.max_length,
                    }),
                );
        }
        return schema;
    })
    .when("$configObj", ([config], schema) => {
        if (config) {
            if (config.password.has_digit) {
                return schema.matches(DIGIT_REGEX, config.t("validation_err_digits"));
            }

            return schema.matches(NO_DIGIT_REGEX, "Digits are not allowed");
        }
        return schema;
    })
    .when("$configObj", ([config], schema) => {
        if (config) {
            if (config.password.has_letter) {
                return schema.matches(LETTERS_REGEX, config.t("validation_err_letters"));
            }

            return schema.matches(NO_LETTERS_REGEX, "Letters are not allowed");
        }
        return schema;
    })
    .when("$configObj", ([config], schema) => {
        if (config && config.password && config.password.required) {
            return schema.required(config.t("validation_err_required"));
        }
        return schema.notRequired().transform((value: string) => (value ? value : null));
    });

export const signupValidationSchema = userInfoValidationSchema
    .pick([
        "username",
        "phone",
        "email",
        "first_name",
        "last_name",
        "birth_date",
        "country",
        "city",
        "state",
        "address",
        "zip_code",
        "gender",
    ])
    .shape({
        referral: string()
            .when("$configObj", ([config], schema) => {
                if (config.referral.required) {
                    if (config.referral.default) {
                        return schema
                            .required(config.t("validation_err_required"))
                            .nullable()
                            .transform((value: string) => (value ? value : null));
                    }
                    return schema.required(config.t("validation_err_required"));
                }
                return schema.notRequired().transform((value: string) => (value ? value : null));
            })
            .when("$configObj", ([config], schema) => {
                return schema.matches(
                    config.referral.dynamic_regexp ?? LETTERS_OR_DIGITS_WITHOUT_UPPER_LETTERS,
                    {
                        excludeEmptyString: true,
                        message: config.t("validation_err_letersOrDigits"),
                    },
                );
            })
            .when("$configObj", ([config], schema) => {
                if (config.referral.min_length && config.referral.max_length) {
                    return schema
                        .min(
                            config.referral.min_length,
                            config.t("validation_err_minMaxLength", {
                                min_length: config.referral.min_length,
                                max_length: config.referral.max_length,
                            }),
                        )
                        .max(
                            config.referral.max_length,
                            config.t("validation_err_minMaxLength", {
                                min_length: config.referral.min_length,
                                max_length: config.referral.max_length,
                            }),
                        );
                }
                return schema;
            }),
        password: PASSWORD_RULE,
        currency: string().when("$configObj", ([config], schema) => {
            if (config.currency.required) {
                return schema.required(config.t("validation_err_required"));
            }
            return schema.notRequired().transform((value: string) => (value ? value : null));
        }),
        language: string().when("$configObj", ([config], schema) => {
            if (config.language.required) {
                return schema.required(config.t("validation_err_required"));
            }
            return schema.notRequired().transform((value: string) => (value ? value : null));
        }),
        acceptTerms: bool().required().oneOf([true]),
    });

export const cpfSignupValidationSchema = signupValidationSchema
    .omit(["city", "state", "address", "zip_code"])
    .shape({
        cpf_number: string()
            .when("$configObj", ([config], schema) => {
                if (config.cpf_number.required) {
                    return schema.required(config.t("validation_err_required"));
                }
                return schema;
            })
            .when("$configObj", ([config], schema) => {
                return schema.min(
                    11,
                    config.t("validation_err_minLength", {
                        min_length: 11,
                    }),
                );
            }),
        referral: string()
            .when("$configObj", ([config], schema) => {
                if (config.referral.required) {
                    return schema.required(config.t("validation_err_required"));
                }
                return schema.notRequired().transform((value: string) => (value ? value : null));
            })
            .when("$configObj", ([config], schema) => {
                return schema
                    .transform((value: string) => value?.toLowerCase())
                    .matches(LETTERS_OR_DIGITS_WITHOUT_UPPER_LETTERS, {
                        excludeEmptyString: true,
                        message: config.t("validation_err_letersOrDigits"),
                    });
            })
            .when("$configObj", ([config], schema) => {
                if (config.referral.min_length && config.referral.max_length) {
                    return schema
                        .min(
                            config.referral.min_length,
                            config.t("validation_err_minMaxLength", {
                                min_length: config.referral.min_length,
                                max_length: config.referral.max_length,
                            }),
                        )
                        .max(
                            config.referral.max_length,
                            config.t("validation_err_minMaxLength", {
                                min_length: config.referral.min_length,
                                max_length: config.referral.max_length,
                            }),
                        );
                }
                return schema;
            }),
        full_name: string()
            .trim()
            .when("$configObj", ([config], schema) => {
                if (config.full_name.max_length) {
                    return schema.max(
                        config.full_name.max_length,
                        config.t("validation_err_maxLength", {
                            max_length: config.full_name.max_length,
                        }),
                    );
                }
                return schema;
            })
            .when("$configObj", ([config], schema) => {
                return schema.matches(WHITESPACE_REGEX, {
                    excludeEmptyString: true,
                    message: config.t("validation_err_fullNameSpaceBetween"),
                });
            })
            .when("$configObj", ([config], schema) => {
                if (config.full_name.required) {
                    return schema.required(config.t("validation_err_required"));
                }
                return schema.notRequired().transform((value: string) => (value ? value : null));
            }),
    });

export const afroSignupValidationSchema = signupValidationSchema.shape({
    age: string().when("$configObj", ([config], schema) => {
        if (config.age.required) {
            return schema.required(config.t("validation_err_required"));
        }
        return schema;
    }),
    password_confirm: string()
        .when("$configObj", ([config], schema) => {
            return schema.required(config.t("validation_err_required"));
        })
        .oneOf([ref("password"), ""], "Passwords must match"),
});
